import { Suspense, useMemo } from "react";

import { Navigate, useLocation } from "react-router-dom";

import BaseComponent from "common/Components/BaseComponent";
import { PlaceholderPanel } from "common/Components/Tabs/CustomSubTabs";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import { NodeView, CloudView } from "common/Constants/route-components";
import NodeList from "pages/DataManagement/NodeList";
import NodeContextImports from "NodeContextImport/NodeContextImports";

import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import { EFFECTIVE_ACCESS_PAGE_KEY } from "common/Constants/EffectiveAccess.constants";
import {
  DATA_MANAGEMENT_DEFAULT_SUB,
  DATA_MANAGEMENT_PATH_BY_SUB,
  getDataManagementSubKeyFromPath,
} from "common/Components/Layout/SideNav.utils";

const getDataManagementSubTabComponent = (key, label) => {
  switch (key) {
    case "edgeHierarchy":
      return NodeView;
    case "cloudHierarchy":
      return CloudView;
    case "tags":
      return NodeList;
    case "imports":
      return NodeContextImports;
    default:
      return () => <PlaceholderPanel label={label} />;
  }
};

const Administration = () => {
  const location = useLocation();

  const subTabKeys = INFRAMARK_CONSTANTS?.dataManagement?.subTabKeys ?? [];
  const subTabNames = INFRAMARK_CONSTANTS?.subTabNames ?? {};
  const dataManagementPath =
    INFRAMARK_CONSTANTS?.navigationRoutes?.dataManagement ?? "/data-management";
  const edgePath =
    INFRAMARK_CONSTANTS?.navigationRoutes?.edge ?? "/data-management/edge-hierarchy";
  const userManagementPath =
    INFRAMARK_CONSTANTS?.navigationRoutes?.userManagement ?? "/user-management";

  const { canAccessPage, usesEffectiveAccess } = useEffectiveAccess();

  const canAccessDataManagement =
    !usesEffectiveAccess ||
    canAccessPage(EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT);
  const canAccessUserManagement =
    !usesEffectiveAccess ||
    canAccessPage(EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT);

  const normalizedPath = String(location.pathname ?? "").replace(/\/+$/, "") || "/";
  const isDataManagementRoot = normalizedPath === dataManagementPath;
  const requestedSub = getDataManagementSubKeyFromPath(location.pathname);
  const activeSubKey = subTabKeys.includes(requestedSub)
    ? requestedSub
    : DATA_MANAGEMENT_DEFAULT_SUB;
  const activeLabel = subTabNames[activeSubKey] ?? activeSubKey;
  const ContentComponent = useMemo(
    () => getDataManagementSubTabComponent(activeSubKey, activeLabel),
    [activeSubKey, activeLabel]
  );

  if (!canAccessDataManagement && canAccessUserManagement) {
    return <Navigate to={userManagementPath} replace />;
  }

  if (!canAccessDataManagement) {
    return <Navigate to="/access_denied" replace />;
  }

  if (isDataManagementRoot) {
    return <Navigate to={edgePath} replace />;
  }

  const canonicalPath = DATA_MANAGEMENT_PATH_BY_SUB[activeSubKey];
  if (canonicalPath && normalizedPath !== canonicalPath) {
    return <Navigate to={canonicalPath} replace />;
  }

  return (
    <BaseComponent>
      <Suspense fallback={<div>Loading...</div>}>
        <ContentComponent />
      </Suspense>
    </BaseComponent>
  );
};

export default Administration;
